import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, Edit, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CoachManagement = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCoach, setEditingCoach] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    email: ''
  });

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/coaches`);
      setCoaches(response.data);
    } catch (error) {
      toast.error('Failed to fetch coaches');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCoach) {
        await axios.put(`${BACKEND_URL}/api/coaches/${editingCoach.phone_number}`, formData);
        toast.success('Coach updated successfully!');
      } else {
        await axios.post(`${BACKEND_URL}/api/coaches`, formData);
        toast.success('Coach added successfully!');
      }
      setDialogOpen(false);
      setFormData({ name: '', phone_number: '', email: '' });
      setEditingCoach(null);
      fetchCoaches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save coach');
    }
  };

  const handleDelete = async (phone_number) => {
    if (!window.confirm('Are you sure you want to delete this coach?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/coaches/${phone_number}`);
      toast.success('Coach deleted successfully!');
      fetchCoaches();
    } catch (error) {
      toast.error('Failed to delete coach');
    }
  };

  const openEditDialog = (coach) => {
    setEditingCoach(coach);
    setFormData({
      name: coach.name,
      phone_number: coach.phone_number,
      email: coach.email || ''
    });
    setDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingCoach(null);
    setFormData({ name: '', phone_number: '', email: '' });
    setDialogOpen(true);
  };

  return (
    <div data-testid="coach-management-page" className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground tracking-tight">COACHES</h1>
          <p className="text-muted-foreground mt-2 font-body">Manage your coaching team</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAddDialog} data-testid="add-coach-btn" className="bg-primary text-primary-foreground hover:brightness-110">
              <Plus className="w-5 h-5 mr-2" />
              Add Coach
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                {editingCoach ? 'Edit Coach' : 'Add New Coach'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-body text-muted-foreground">Name</label>
                <Input
                  data-testid="coach-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  className="bg-input border-transparent focus:border-primary"
                />
              </div>
              <div>
                <label className="text-sm font-body text-muted-foreground">Phone Number</label>
                <Input
                  data-testid="coach-phone-input"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  placeholder="1234567890"
                  required
                  disabled={!!editingCoach}
                  className="bg-input border-transparent focus:border-primary"
                />
              </div>
              <div>
                <label className="text-sm font-body text-muted-foreground">Email (Optional)</label>
                <Input
                  data-testid="coach-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  className="bg-input border-transparent focus:border-primary"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" data-testid="save-coach-btn" className="flex-1 bg-primary text-primary-foreground hover:brightness-110">
                  {editingCoach ? 'Update' : 'Add'} Coach
                </Button>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-border">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="bg-card border border-border p-6 animate-pulse">
              <div className="h-24 bg-muted rounded"></div>
            </Card>
          ))}
        </div>
      ) : coaches.length === 0 ? (
        <Card className="bg-card border border-border p-12 text-center">
          <p className="text-muted-foreground">No coaches added yet. Add your first coach to get started!</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {coaches.map((coach) => (
            <Card key={coach.phone_number} data-testid={`coach-card-${coach.phone_number}`} className="bg-card border border-border p-6 hover:border-primary/50 transition-colors duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-md flex items-center justify-center">
                  <span className="text-primary font-heading font-bold text-xl">
                    {coach.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid={`edit-coach-${coach.phone_number}`}
                    onClick={() => openEditDialog(coach)}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    data-testid={`delete-coach-${coach.phone_number}`}
                    onClick={() => handleDelete(coach.phone_number)}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-3">{coach.name}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="w-4 h-4" />
                  <span>+{coach.phone_number}</span>
                </div>
                {coach.email && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    <span>{coach.email}</span>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CoachManagement;
